import { ColumnDef } from '@tanstack/react-table';
import React from 'react';
import { cn } from '../../../../../utils/styles';
import { IAccordionData } from '../../../Common/AccordionTable';
import { GuidanceViewPdfButton, GuidanceViewWebButton } from '../../Dashboard/Data';
import { PdfViewerTrigger } from '../../../../../context/pdfViewerDialog';
import Icons from '../../../../../components/Common/Icons/Icons';
import { Button } from '../../../../../components/Common/UI/Button';

function formatDateShort(date: Date): string {
  const options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
  };

  return date.toLocaleDateString('en-US', options);
}

function getDateForMonth(year: number, monthIndex: number, day: number): string {
  const date = new Date(year, monthIndex, day);

  return formatDateShort(date);
}

const currentYear = new Date().getFullYear();
const currentMonth = new Date().getMonth(); // 0-11

const allInserts = [
  { label: 'Go Paperless', href: '/guidance/pdfs/PaperlessInsert.pdf' },
  // { label: 'Credit Card Offer', href: '/guidance/statements/CreditCardOffer.pdf' },
  // { label: 'Amex', href: '/guidance/statements/Amex.pdf' },
  // { label: 'Car Rental Offer', href: '/guidance/statements/RentalCarOffer.pdf' },
  // { label: 'High Yield CD', href: '/guidance/statements/CarYieldCD.pdf' },
  // { label: 'Retirement Account', href: '/guidance/statements/RetirementAccount.pdf' },
  // { label: 'Monitor Credit Score', href: '/guidance/statements/MonitorCreditScore.pdf' },
  // { label: 'Privacy Policy', href: '/guidance/statements/PrivacyPolicy.pdf' },
  // { label: 'Money Market Account', href: '/guidance/statements/MoneyMarket.pdf' },
  // { label: 'CD Account', href: '/guidance/statements/CDAccount.pdf' },
  // { label: 'Go Paperless', href: '/guidance/statements/PaperlessInsert.pdf' },
  // { label: 'Car Loan', href: '/guidance/statements/CarLoan.pdf' },
  // { label: 'Home Loan', href: '/guidance/statements/HomeLoan.pdf' },
  // { label: 'Monthly Newsletter', href: '/guidance/statements/MonthlyNewsletter.pdf' },
];

export const SEARCH_SUGGESTIONS = ['Payroll'];

function getRandomInserts(): { label: string; href: string }[] {
  const count = Math.floor(Math.random() * 2) + 1;
  const shuffled = [...allInserts].sort(() => 0.5 - Math.random());

  return shuffled.slice(0, count);
}

const statementsMonthsConfig = [
  {
    monthIndex: 11,
    day: 31,
    description: 'Statements',
    insert: [
      { label: 'Go Paperless', href: '/guidance/pdfs/PaperlessInsert.pdf' },
      { label: 'CD Account', href: '/guidance/pdfs/CDaccount.pdf' },
    ],
  },
  {
    monthIndex: 10,
    day: 30,
    description: 'Statements',
    insert: getRandomInserts(),
  },
  {
    monthIndex: 9,
    day: 31,
    description: 'Statements',
    insert: getRandomInserts(),
  },
  {
    monthIndex: 8,
    day: 30,
    description: 'Statements',
    insert: getRandomInserts(),
  },
  {
    monthIndex: 7,
    day: 31,
    description: 'Statements',
    insert: [
      { label: 'Home Loan', href: '/guidance/pdfs/homeLoan.pdf' },
      { label: 'Car Loan', href: '/guidance/pdfs/carLoan.pdf' },
    ],
  },
  {
    monthIndex: 6,
    day: 31,
    description: 'Statements',
    insert: [{ label: 'Car Rental Offer', href: '/guidance/pdfs/CarRental.pdf' }],
  },
  {
    monthIndex: 5,
    day: 30,
    description: 'Statements',
    insert: [
      { label: 'Go Paperless', href: '/guidance/pdfs/PaperlessInsert.pdf' },
      { label: 'CD Account', href: '/guidance/pdfs/CDaccount.pdf' },
    ],
  },
  {
    monthIndex: 4,
    day: 31,
    description: 'Statements',
    insert: [
      { label: 'Home Loan', href: '/guidance/pdfs/homeLoan.pdf' },
      { label: 'Car Loan', href: '/guidance/pdfs/carLoan.pdf' },
    ],
  },
  {
    monthIndex: 3,
    day: 30,
    description: 'Statements',
    insert: [{ label: 'Car Rental Offer', href: '/guidance/pdfs/CarRental.pdf' }],
  },
  {
    monthIndex: 2,
    day: 31,
    description: 'Statements',
    insert: [
      { label: 'Home Loan', href: '/guidance/pdfs/homeLoan.pdf' },
      { label: 'Car Loan', href: '/guidance/pdfs/carLoan.pdf' },
    ],
  },
  {
    monthIndex: 1,
    day: 28,
    description: 'Statements',
    insert: getRandomInserts(),
  },
  {
    monthIndex: 0,
    day: 31,
    description: 'Statements',
    insert: getRandomInserts(),
  },
];

function generateStatementsData(year: number): IStatementsData[] {
  const availableMonths =
    year === currentYear ? statementsMonthsConfig.filter(m => m.monthIndex < currentMonth) : statementsMonthsConfig;

  return availableMonths.map((config, index) => ({
    id: String(index + 1),
    date: getDateForMonth(year, config.monthIndex, config.day),
    description: config.description,
    insert: config.insert,
  }));
}

export interface IStatementsData {
  id: string;
  date: string;
  description: string;
  insert: { label: string; href: string }[];
  type?: 'Payroll' | string;
  pdfSrc?: string;
}

export const statementsCol: ColumnDef<IStatementsData>[] = [
  {
    header: 'Statements date',
    accessorKey: 'date',
    cell: ({ row }) => (
      <div className="flex items-center gap-[10px]">
        <div
          className={cn('w-[8px] h-[8px] rounded uppercase', {
            'bg-addition8': !!row.original.insert.length,
          })}
        />
        {row.getValue('date')}
      </div>
    ),
  },
  {
    header: 'Description',
    accessorKey: 'description',
  },
  {
    header: 'Inserts',
    accessorKey: 'insert',
    size: 230,
    cell: ({ row }) => (
      <div className="flex flex-col items-end md:items-start">
        {(row.original.insert || []).map(insert => (
          <PdfViewerTrigger src={insert.href} key={insert.label}>
            <p aria-label="download-pdf" className="cursor-pointer text-addition6 hover:text-addition7 underline">
              {insert.label}
            </p>
          </PdfViewerTrigger>
        ))}
      </div>
    ),
  },
  {
    id: 'actions',
    accessorKey: '',
    header: 'Actions',
    size: 300,
    cell: ({ row }) => {
      const { type } = row.original;

      const webSrc =
        type === 'Payroll'
          ? '/UnreviewedDocumentPage/December-Statement_Search.html'
          : '/UnreviewedDocumentPage/December-Statement_eStatements.html';

      const pdfSrc = row.original.pdfSrc || '/guidance/pdfs/CheckingStatement2026.pdf';

      return (
        <div className="flex gap-2 sm:gap-4 flex-grow w-full sm:justify-between">
          <GuidanceViewPdfButton src={pdfSrc} />
          <GuidanceViewWebButton src={webSrc} />
        </div>
      );
    },
  },
];

const statementsData2026 = generateStatementsData(currentYear);
const statementsData2025 = generateStatementsData(currentYear - 1);
const statementsData2024 = generateStatementsData(currentYear - 2);
const statementsData2023 = generateStatementsData(currentYear - 3);

const lastAvailableDate2026 = statementsData2026[0]?.date || getDateForMonth(currentYear, 0, 15);

const statementsData2026WithSearch = [
  ...statementsData2026,
  {
    id: 'ach-1',
    date: lastAvailableDate2026,
    description: 'Statements',
    insert: [
      { label: 'Home Loan', href: '/guidance/pdfs/homeLoan.pdf' },
      { label: 'Car Loan', href: '/guidance/pdfs/carLoan.pdf' },
    ],
    type: 'Payroll',
    pdfSrc: '/guidance/pdfs/CheckingStatement2026.pdf',
  },
];

export const statementsAccordion: IAccordionData<IStatementsData>[] = [
  {
    id: '1',
    label: `Statements from ${currentYear}`,
    year: currentYear,
    data: statementsData2026WithSearch,
  },
  {
    id: '2',
    label: `Statements from ${currentYear - 1}`,
    year: currentYear - 1,
    data: statementsData2025,
  },
  {
    id: '3',
    label: `Statements from ${currentYear - 2}`,
    year: currentYear - 2,
    data: statementsData2024,
  },
  {
    id: '4',
    label: `Statements from ${currentYear - 3}`,
    year: currentYear - 3,
    data: statementsData2023,
  },
];

/** Mortgage columns */
export const mortgageStatementsCol: ColumnDef<IStatementsData>[] = statementsCol.map(item =>
  item.id === 'actions'
    ? {
        id: 'actions',
        accessorKey: '',
        header: 'Actions',
        size: 346,
        cell: ({ row }) => {
          const { type } = row.original;

          const webSrc =
            type === 'Payroll'
              ? '/UnreviewedDocumentPage/December-Statement_Search.html'
              : '/UnreviewedDocumentPage/December-Statement_eStatements.html';

          const pdfSrc = row.original.pdfSrc || '/guidance/pdfs/MortgageStatement.pdf';

          return (
            <div className="flex gap-2 sm:gap-4 flex-grow w-full sm:justify-between">
              <GuidanceViewPdfButton src={pdfSrc} />
              <GuidanceViewWebButton src={webSrc} />
              <Button size="auto" variant="ghost">
                <Icons.CardPayment />
              </Button>
            </div>
          );
        },
      }
    : item
);

const mortgageNewDates = [
  'Jul 5',
  'Jun 11',
  'May 5',
  'Apr 11',
  'Mar 05',
  'Feb 11',
  'Jan 05',
  'Dec 31',
  'Nov 30',
  'Oct 31',
  'Sep 30',
  'Aug 31',
];

const monthMap: { [key: string]: number } = {
  Jan: 0,
  Feb: 1,
  Mar: 2,
  Apr: 3,
  May: 4,
  Jun: 5,
  Jul: 6,
  Aug: 7,
  Sep: 8,
  Oct: 9,
  Nov: 10,
  Dec: 11,
};

const mortgageStatementsDataFiltered: IStatementsData[] = statementsMonthsConfig
  .map((config, index) => ({
    id: String(index + 1),
    date: mortgageNewDates[index] || getDateForMonth(currentYear, config.monthIndex, config.day),
    description: config.description,
    insert: config.insert,
  }))
  .filter(item => {
    const itemMonth = monthMap[item.date.split(' ')[0]];

    return itemMonth <= currentMonth - 1;
  })
  .sort((a, b) => {
    const monthA = monthMap[a.date.split(' ')[0]];
    const monthB = monthMap[b.date.split(' ')[0]];

    return monthB - monthA;
  });

const mortgageStatementsDataAllMonths: IStatementsData[] =
  mortgageStatementsDataFiltered.length > 0
    ? mortgageStatementsDataFiltered
    : [
        statementsMonthsConfig
          .map((config, index) => ({
            id: String(index + 1),
            date: mortgageNewDates[index] || getDateForMonth(currentYear, config.monthIndex, config.day),
            description: config.description,
            insert: config.insert,
          }))
          .sort((a, b) => {
            const monthA = monthMap[a.date.split(' ')[0]];
            const monthB = monthMap[b.date.split(' ')[0]];

            return monthB - monthA;
          })[0],
      ].filter(Boolean);

const lastAvailableMortgageDate = mortgageStatementsDataAllMonths[0]?.date || getDateForMonth(currentYear, 0, 15);

const mortgageStatementsDataWithSearch: IStatementsData[] = [
  ...mortgageStatementsDataAllMonths,
  {
    id: 'ach-1',
    date: lastAvailableMortgageDate,
    description: 'Statements',
    insert: [
      { label: 'Credit Card Offer', href: '/guidance/otherDocs/bill_highlighted.pdf' },
      { label: 'Amex', href: '/guidance/otherDocs/bill_highlighted.pdf' },
    ],
    type: 'Payroll',
    pdfSrc: '/guidance/otherDocs/bill_highlighted.pdf',
  },
];

export const mortgageStatementsAccordion: IAccordionData<IStatementsData>[] = [
  {
    id: '1',
    label: `Statements from ${currentYear}`,
    year: currentYear,
    data: mortgageStatementsDataWithSearch,
  },
  {
    id: '2',
    label: `Statements from ${currentYear - 1}`,
    year: currentYear - 1,
    data: statementsData2025,
  },
  {
    id: '3',
    label: `Statements from ${currentYear - 2}`,
    year: currentYear - 2,
    data: statementsData2024,
  },
  {
    id: '4',
    label: `Statements from ${currentYear - 3}`,
    year: currentYear - 3,
    data: statementsData2023,
  },
];

const VISA_CLASSIC_PDF = '/guidance/pdfs/VisaClassicStatement.pdf';

/** Visa Classic columns — same layout as Checking, View PDF opens Visa Classic statement */
export const visaClassicStatementsCol: ColumnDef<IStatementsData>[] = statementsCol.map(item =>
  item.id === 'actions'
    ? {
        id: 'actions',
        accessorKey: '',
        header: 'Actions',
        size: 300,
        cell: ({ row }) => {
          const { type } = row.original;

          const webSrc =
            type === 'Payroll'
              ? '/UnreviewedDocumentPage/December-Statement_Search.html'
              : '/UnreviewedDocumentPage/December-Statement_eStatements.html';

          const pdfSrc = row.original.pdfSrc || VISA_CLASSIC_PDF;

          return (
            <div className="flex gap-2 sm:gap-4 flex-grow w-full sm:justify-between">
              <GuidanceViewPdfButton src={pdfSrc} />
              <GuidanceViewWebButton src={webSrc} />
            </div>
          );
        },
      }
    : item
);

/** Same inserts as Checking Statements; each row opens the Visa Classic PDF */
export const visaClassicStatementsAccordion: IAccordionData<IStatementsData>[] = statementsAccordion.map(section => ({
  ...section,
  data: section.data.map(row => ({
    ...row,
    pdfSrc: VISA_CLASSIC_PDF,
  })),
}));
